/**
 * PivotPointsListView — Compact tap-to-vote grid of pivot points.
 *
 * Fourth view mode alongside Kaikki / Klusterit / Puurakenne. Designed to
 * fit a typical workshop's pivot points on one screen with no scroll —
 * cards are auto-flowed into columns so 12 pivots come out 3×4 ish on a
 * standard laptop. Title only, vote button + count, click background to
 * open the detail modal.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { ThumbsUp, Sparkles, User } from 'lucide-react';
import API_ENDPOINTS, { withBackendPath } from '../../config/api';
import { getOrCreateParticipantId } from '../../utils/participantId';
import socketService from '../../../../../packages/shared/socket';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const Container = styled.div`
  height: 100%;
  overflow-y: auto;
  padding: 32px 64px 48px;
  background: #0a0a14;

  @media (max-width: 900px) {
    padding: 24px 32px 32px;
  }
  @media (max-width: 600px) {
    padding: 20px 16px 24px;
  }
`;

const Inner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.85rem;
`;

const Count = styled.span`
  color: rgba(255, 255, 255, 0.4);
`;

const Grid = styled.div`
  display: grid;
  /* Up to 3 columns on wide screens; collapses to 2 then 1 on narrower
     viewports so the title + tap target stay legible without scrolling. */
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const VOTED_GREEN = '#16a34a';
const VOTED_GREEN_BG = 'rgba(22, 163, 74, 0.18)';
const VOTED_GREEN_BORDER = 'rgba(22, 163, 74, 0.65)';

const Card = styled.button`
  text-align: left;
  background: ${p => p.$voted ? VOTED_GREEN_BG : 'rgba(255, 255, 255, 0.04)'};
  border: 1px solid ${p => p.$voted ? VOTED_GREEN_BORDER : 'rgba(255, 255, 255, 0.08)'};
  border-radius: 10px;
  padding: 14px 14px 14px 16px;
  cursor: pointer;
  font-family: inherit;
  color: #fff;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  align-items: center;
  min-height: 84px;
  transition: background 0.15s, border-color 0.15s, transform 0.1s;

  &:hover {
    background: ${p => p.$voted ? 'rgba(22, 163, 74, 0.28)' : 'rgba(255, 255, 255, 0.08)'};
    border-color: ${p => p.$voted ? VOTED_GREEN : 'rgba(255, 255, 255, 0.22)'};
  }
  &:active { transform: scale(0.99); }
  &:focus-visible {
    outline: 2px solid #7c3aed;
    outline-offset: 2px;
  }
`;

const TitleCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const Title = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.35;
  color: rgba(255, 255, 255, 0.92);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

// AI-discovered vs facilitator-created pivot — keeps the origin transparent.
const SourceBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  align-self: flex-start;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  padding: 2px 7px;
  border-radius: 10px;
  background: ${p => p.$ai ? 'rgba(124, 58, 237, 0.16)' : 'rgba(96, 165, 250, 0.14)'};
  color: ${p => p.$ai ? '#c4b5fd' : '#93c5fd'};
  border: 1px solid ${p => p.$ai ? 'rgba(124, 58, 237, 0.4)' : 'rgba(96, 165, 250, 0.35)'};
`;

const VoteButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  padding: 0;
  border-radius: 12px;
  border: 1px solid ${p => p.$voted ? VOTED_GREEN : 'rgba(255, 255, 255, 0.16)'};
  background: ${p => p.$voted ? VOTED_GREEN : 'rgba(255, 255, 255, 0.08)'};
  color: ${p => p.$voted ? '#fff' : 'rgba(255, 255, 255, 0.75)'};
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, transform 0.1s;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: ${p => p.$voted ? '#15803d' : 'rgba(255, 255, 255, 0.16)'};
    border-color: ${p => p.$voted ? '#15803d' : 'rgba(255, 255, 255, 0.35)'};
  }
  &:active:not(:disabled) { transform: scale(0.94); }
  &:disabled { opacity: 0.6; cursor: progress; }
`;

const CountBadge = styled.span`
  position: absolute;
  top: -7px;
  right: -8px;
  min-width: 22px;
  height: 20px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${VOTED_GREEN};
  color: #fff;
  font-size: 0.72rem;
  font-weight: 800;
  line-height: 1;
  border-radius: 999px;
  border: 1.5px solid #0a0a14;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
  pointer-events: none;
`;

const Empty = styled.div`
  padding: 60px 20px;
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.9rem;
`;

export default function PivotPointsListView({
  pivotPoints = [],
  instanceId = null,
  onPivotClick,
}) {
  const { t } = useTranslation();
  const [counts, setCounts] = useState({});
  const [myVotes, setMyVotes] = useState({});
  const [canVote, setCanVote] = useState(false);
  const [pendingVotes, setPendingVotes] = useState(new Set());

  // Stable per-device participantId for vote attribution. Generates one on
  // first load if not present — covers PIN-authenticated devices that never
  // went through /impactpath (which used to be the only path that wrote it).
  // Backend prefers Entra cookie when present, so this is harmless for
  // signed-in users.
  const participantId = getOrCreateParticipantId();
  const voteHeaders = participantId ? { 'X-Participant-Id': participantId } : undefined;

  useEffect(() => {
    if (!instanceId) return;
    let cancelled = false;
    fetch(API_ENDPOINTS.PIVOT_POINT_VOTES(instanceId), {
      credentials: 'include',
      headers: voteHeaders,
    })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (cancelled || !data) return;
        setCounts(data.counts || {});
        setMyVotes(data.myVotes || {});
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [instanceId]);

  // Live vote updates — when any device casts a vote, the backend broadcasts
  // updated counts to the instance room. Wall screens + other iPads update
  // counts in real time, no refresh needed. Only counts are broadcast;
  // per-device myVotes stays local.
  useEffect(() => {
    if (!instanceId) return;
    const handler = (data) => {
      if (!data || data.instanceId !== instanceId) return;
      setCounts(data.counts || {});
    };
    socketService.socket?.on('pivot-vote-updated', handler);
    return () => {
      socketService.socket?.off('pivot-vote-updated', handler);
    };
  }, [instanceId]);

  useEffect(() => {
    let cancelled = false;
    // canVote = Entra session OK OR a PIN participantId is present. The
    // backend resolves voter identity from either source.
    if (participantId) {
      setCanVote(true);
      return () => { cancelled = true; };
    }
    fetch(withBackendPath('/auth/me'), { credentials: 'include' })
      .then(r => { if (!cancelled) setCanVote(r.ok); })
      .catch(() => { if (!cancelled) setCanVote(false); });
    return () => { cancelled = true; };
  }, [participantId]);

  // Single-vote semantics: each user has at most one active vote per
  // instance. Clicking pivot B while A is voted moves the vote; clicking
  // the same pivot again toggles it off. Server returns the full fresh
  // {counts, myVotes} so we don't have to compute the diff client-side.
  const toggleVote = useCallback(async (pivotId) => {
    if (!instanceId || !canVote) return;
    setPendingVotes(p => { const n = new Set(p); n.add(pivotId); return n; });

    const prevCounts = counts;
    const prevMyVotes = myVotes;
    const previousVotedId = Object.entries(myVotes).find(([, v]) => v)?.[0] || null;
    const isToggleOff = previousVotedId === pivotId;
    const newVotedId = isToggleOff ? null : pivotId;

    // Optimistic update — apply the move locally
    const nextCounts = { ...counts };
    if (previousVotedId && previousVotedId !== newVotedId) {
      nextCounts[previousVotedId] = Math.max(0, (nextCounts[previousVotedId] || 0) - 1);
    }
    if (newVotedId && newVotedId !== previousVotedId) {
      nextCounts[newVotedId] = (nextCounts[newVotedId] || 0) + 1;
    }
    setCounts(nextCounts);
    setMyVotes(newVotedId ? { [newVotedId]: true } : {});

    try {
      const r = await fetch(API_ENDPOINTS.PIVOT_POINT_VOTE(instanceId, pivotId), {
        method: 'POST',
        credentials: 'include',
        headers: voteHeaders,
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      if (data?.counts) setCounts(data.counts);
      if (data?.myVotes) setMyVotes(data.myVotes);
    } catch (err) {
      console.error('[pivot-vote] failed', err);
      setCounts(prevCounts);
      setMyVotes(prevMyVotes);
    } finally {
      setPendingVotes(p => { const n = new Set(p); n.delete(pivotId); return n; });
    }
  }, [instanceId, canVote, counts, myVotes, participantId]);

  // Sort once by member count (descending). Vote counts deliberately don't
  // affect order — keeping cards in place while voting is better UX than
  // having them shuffle around on every click.
  const sorted = useMemo(() =>
    [...pivotPoints].sort((a, b) => {
      const ma = a.memberCount ?? a.memberIds?.length ?? a.size ?? 0;
      const mb = b.memberCount ?? b.memberIds?.length ?? b.size ?? 0;
      return mb - ma;
    }),
    [pivotPoints]
  );

  if (sorted.length === 0) {
    return (
      <Container>
        <Inner>
          <Empty>{t('pivotPoints.list.empty')}</Empty>
        </Inner>
      </Container>
    );
  }

  return (
    <Container>
      <Inner>
        <Header>
          <span>
            {canVote
              ? t('pivotPoints.list.voteHint')
              : t('pivotPoints.list.loginHint', { count: sorted.length })}
          </span>
          <Count>{t('pivotPoints.list.count', { count: sorted.length })}</Count>
        </Header>

        <Grid>
          {sorted.map(p => {
            const title = p.text || p.title || p.representative_text || t('pivotPoints.common.pivotPoint');
            const voteCount = counts[p.id] || 0;
            const voted = !!myVotes[p.id];
            const pending = pendingVotes.has(p.id);
            const isAi = (p.source || 'ai') === 'ai';

            return (
              <Card
                key={p.id}
                $voted={voted}
                onClick={() => onPivotClick?.(p)}
                title={t('pivotPoints.list.openDetails')}
              >
                <TitleCell>
                  <SourceBadge $ai={isAi}>
                    {isAi ? <Sparkles size={10} /> : <User size={10} />}
                    {isAi ? 'AI' : t('pivotPoints.common.human')}
                  </SourceBadge>
                  <Title>{title}</Title>
                </TitleCell>
                {canVote ? (
                  <div onClick={e => e.stopPropagation()}>
                    <VoteButton
                      $voted={voted}
                      disabled={pending}
                      onClick={() => toggleVote(p.id)}
                      aria-pressed={voted}
                      title={voted ? t('pivotPoints.list.unvote') : t('pivotPoints.list.vote')}
                    >
                      <ThumbsUp size={20} strokeWidth={2.2} />
                      {voteCount > 0 && <CountBadge>{voteCount}</CountBadge>}
                    </VoteButton>
                  </div>
                ) : (
                  <div style={{
                    position: 'relative',
                    width: 52,
                    height: 52,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'rgba(255,255,255,0.45)',
                  }}>
                    <ThumbsUp size={20} strokeWidth={2.2} />
                    {voteCount > 0 && <CountBadge>{voteCount}</CountBadge>}
                  </div>
                )}
              </Card>
            );
          })}
        </Grid>
      </Inner>
    </Container>
  );
}
